import type { ManualStatusConfigurationValue } from './ManualStatusConfiguration';
import { formatManualJobSelectValue, MANUAL_WELDING_MACHINE_ID } from './manualJobNumberFormat';

/**
 * Merge the active job's live config into the per-job cache (synchronous).
 */
export function mergeJobConfigurationCache(
  jobConfigurations: Record<string, ManualStatusConfigurationValue>,
  currentJobKey: string | null | undefined,
  currentConfiguration: ManualStatusConfigurationValue | null | undefined
): Record<string, ManualStatusConfigurationValue> {
  if (!currentJobKey || !currentConfiguration) {
    return { ...jobConfigurations };
  }
  return {
    ...jobConfigurations,
    [currentJobKey]: currentConfiguration,
  };
}

/**
 * Pick configurations that should be persisted on Save.
 * Includes every dirty job key present in the cache; if none are dirty,
 * falls back to the active job (single-job save).
 */
export function selectManualConfigurationsToSave(
  allConfigurations: Record<string, ManualStatusConfigurationValue>,
  dirtyJobKeys: Iterable<string>,
  currentJobKey: string | null | undefined,
  currentIsDirty: boolean
): ManualStatusConfigurationValue[] {
  const dirtyKeys = new Set(dirtyJobKeys);
  if (currentIsDirty && currentJobKey) {
    dirtyKeys.add(currentJobKey);
  }

  const selected: ManualStatusConfigurationValue[] = [];
  const seenJobNumbers = new Set<string>();

  const pushConfig = (key: string) => {
    const config = allConfigurations[key];
    if (!config) return;
    const jobNumber = config.jobNumber?.trim();
    if (!jobNumber || seenJobNumbers.has(jobNumber.toLowerCase())) return;
    seenJobNumbers.add(jobNumber.toLowerCase());
    selected.push(config);
  };

  if (dirtyKeys.size > 0) {
    dirtyKeys.forEach((key) => pushConfig(key));
    return selected;
  }

  if (currentJobKey) {
    pushConfig(currentJobKey);
  }

  return selected;
}

/**
 * Display key used in ManualStatusConfiguration's jobConfigurations map.
 */
export function toManualJobCacheKey(jobNumberOrDisplay: string): string {
  if (!jobNumberOrDisplay) return '';
  if (jobNumberOrDisplay.includes(':')) return jobNumberOrDisplay;
  return formatManualJobSelectValue(MANUAL_WELDING_MACHINE_ID, jobNumberOrDisplay);
}

/**
 * Which automated status-config job options to include in a single Save request.
 * Uses every option that has a cached config, always including the active job.
 */
export function selectStatusJobOptionsToSave<T extends { value: string }>(
  jobOptions: T[],
  selectedJobValue: string,
  cachedConfigKeys: Iterable<string>,
  getConfigKey: (jobValue: string) => string
): T[] {
  const keys = new Set(cachedConfigKeys);
  if (selectedJobValue) {
    keys.add(getConfigKey(selectedJobValue));
  }

  const matched = jobOptions.filter((option) => keys.has(getConfigKey(option.value)));
  if (matched.length > 0) {
    return matched;
  }

  return jobOptions.filter((option) => option.value === selectedJobValue);
}
